// Global data: cross-project reference material shown to learning agents. Drive folders
// and tracked Drive files sync through useGlobalDriveSync; rows of kind "file" are the
// directly uploaded files being retired.
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAgentsHttp } from "../useAgentsHttp";
import { QK } from "../queryKeys";
import type { DriveSyncState } from "./useDriveSync";

export type GlobalDataKind = "file" | "drive_folder" | "drive_file";

type DriveFolderSyncConfigKey = "exclude_name_patterns" | "enabled";

// A project's drive sync shape with its single folder in place of roots and tracked
// files; the last-sync keys are absent until the folder's first sync lands.
export type DriveFolderSync = Pick<DriveSyncState, DriveFolderSyncConfigKey> & {
  folder_id: string;
  folder_name: string;
  excluded_subfolder_ids: string[];
  // Per-file exclusions keyed by Drive file id, each with who excluded it and when.
  excluded_drive_file_ids?: Record<string, unknown>;
  // Added but never synced: paused until someone reviews its exclusions and syncs it.
  awaiting_review?: boolean;
  added_by?: { name?: string; email?: string } | null;
  added_at?: string | null;
} & Partial<Omit<DriveSyncState, DriveFolderSyncConfigKey | "roots" | "files">>;

export interface GlobalDataFileCounts {
  ready: number;
  extracting: number;
  failed: number;
  removed: number;
}

export interface GlobalDataRecord {
  global_data_id: string;
  title: string;
  context: string;
  agent_ids: string[];
  // The learning agents (the project team) wired to open this file. Separate
  // from the legacy roster's ids, which share a key with them ("grid").
  learning_agent_ids: string[];
  tenant_scope: string | null;
  landlord_scope: string | null;
  original_name: string;
  content_type: string;
  s3_key: string;
  size_bytes: number | null;
  status: "pending" | "ready" | string;
  // One-paragraph "what's in this file" summary, or null if not generated.
  summary: string | null;
  // "text" (cheap parse), "llm" (vision/transcription fallback), "none".
  extraction_method: "text" | "llm" | "none" | null;
  // True when the extracted text was capped (not everything captured).
  extraction_truncated: boolean;
  // Whether there's any extracted text to view.
  has_extracted_text: boolean;
  // The worker's processing state.
  requirements_status: "queued" | "pending" | "running" | "ready" | "failed" | "none" | null;
  // Coarse worker phase while requirements_status is pending/running (Reading ->
  // Summarising), so a large deferred PDF shows live progress instead
  // of a static spinner. Null when not processing. Mirrors the BE row.progress.
  progress: { label: string; done: number | null; total: number | null } | null;
  // Whether this row's single AI trace (the whole post-complete operation -
  // OCR or vision and the summary) was persisted.
  // Null/false for records produced before trace capture -> the "View AI
  // trace" button is hidden. Mirrors the BE ``has_ai_trace_summary`` column.
  has_ai_trace_summary: boolean | null;
  created_at: string;
  created_by: string | null;
  updated_at: string;
  kind: GlobalDataKind;
  parent_global_data_id: string | null;
  drive_file_id: string | null;
  drive_path: string | null;
  removal_reason: string | null;
  drive_sync: DriveFolderSync | null;
  // A drive_folder's in-flight Sync now, so its card re-attaches the progress bar.
  active_sync_operation_key?: string | null;
  file_counts: GlobalDataFileCounts | null;
}

export type GlobalDriveFileState = keyof GlobalDataFileCounts;

const EXTRACTING_REQUIREMENTS_STATUSES = new Set(["queued", "pending", "running"]);

export const isRequirementsExtracting = (
  status: GlobalDataRecord["requirements_status"],
): boolean => status !== null && EXTRACTING_REQUIREMENTS_STATUSES.has(status);

// Mirrors the BE ``folder_file_counts`` buckets, so a folder's counts agree with the
// synced files card.
export const globalDriveFileState = (file: GlobalDataRecord): GlobalDriveFileState => {
  if (file.status === "removed") return "removed";
  if (file.requirements_status === "failed") return "failed";
  if (isRequirementsExtracting(file.requirements_status)) return "extracting";
  return "ready";
};

export interface GlobalDataExtraction {
  global_data_id: string;
  title: string;
  original_name: string;
  extracted_text: string | null;
  extraction_method: "text" | "llm" | "none" | null;
  extraction_truncated: boolean;
}

interface GlobalDataListResponse {
  global_data: GlobalDataRecord[];
}

interface DownloadUrlResponse {
  url: string;
  original_name: string;
  content_type: string;
}

const QUERY_KEY = QK.globalDataList;

// ``poll`` keeps the list refreshing while a caller knows work is in flight that
// the rows can't show yet, e.g. a Drive folder sync still walking the folder.
export const useGlobalDataList = (options?: { poll?: boolean }) => {
  const http = useAgentsHttp();
  return useQuery<GlobalDataRecord[]>(
    [QUERY_KEY],
    async () => {
      const resp = await http<GlobalDataListResponse>("/global-data/list", {});
      return resp.global_data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      refetchInterval: (data) =>
        options?.poll ||
        (data ?? []).some(
          (r) =>
            r.status === "pending" ||
            isRequirementsExtracting(r.requirements_status) ||
            (r.file_counts?.extracting ?? 0) > 0,
        )
          ? 4000
          : false,
    },
  );
};

export const useDeleteGlobalData = () => {
  const http = useAgentsHttp();
  const qc = useQueryClient();
  return useMutation<{ deleted: string }, Error, string>(
    async (global_data_id) =>
      http<{ deleted: string }>("/global-data/delete", { global_data_id }),
    {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: [QUERY_KEY] });
        qc.invalidateQueries({ queryKey: [QK.globalDriveBrowse] });
        qc.invalidateQueries({ queryKey: [QK.globalDriveFiles] });
      },
    },
  );
};

export const useGlobalDataDownloadUrl = () => {
  const http = useAgentsHttp();
  return useMutation<DownloadUrlResponse, Error, string>(
    async (global_data_id) =>
      http<DownloadUrlResponse>("/global-data/download_url", {
        global_data_id,
      }),
  );
};

// A global file's extracted text, for the Complete source modal's text tab. Kept
// off the list payload since it can be large; fetched only once the tab opens.
export const useGlobalDataText = (global_data_id: string | null | undefined) => {
  const http = useAgentsHttp();
  return useQuery<{ text: string }>(
    [QK.globalDataText, global_data_id ?? null],
    async () => {
      const resp = await http<GlobalDataExtraction>("/global-data/extraction", {
        global_data_id,
      });
      return { text: resp.extracted_text ?? "" };
    },
    { enabled: Boolean(global_data_id), staleTime: 60_000 },
  );
};
